import { BusinessOperatingSystem } from '@ninety/mobile/platform/api';

import {
  DiscussionComment,
  DiscussionForm,
  DiscussionFormalQuestion,
  DiscussionModel,
  DiscussionType,
  ValueRating,
  YesNoMaybe,
} from '../discussion.models';
import { DiscussionFormMode, DiscussionFormSectionKey, DiscussionFormValue } from '../form/discussion-form.model';

type OptionalDiscussionFormQuestions = Partial<Pick<DiscussionModel, 'quarterlyQuestions' | 'meetingQuestions'>>;
type SaveDiscussionFormData = Pick<DiscussionModel, '_id' | 'isManagerDone' | 'isManageeDone' | 'comments'> &
  OptionalDiscussionFormQuestions;

export abstract class SaveDiscussionFormDto implements SaveDiscussionFormData {
  _id!: string;
  comments!: DiscussionComment[];
  quarterlyQuestions?: DiscussionFormalQuestion[];
  meetingQuestions?: DiscussionFormalQuestion[];
  isManagerDone?: boolean;
  isManageeDone?: boolean;

  abstract managerDiscussionForm?: DiscussionForm;
  abstract manageeDiscussionForm?: DiscussionForm;

  protected constructor(discussion: DiscussionModel, formValue: DiscussionFormValue) {
    const { _id, comments, quarterlyQuestions, meetingQuestions } = discussion;

    this._id = _id ?? '';
    this.comments = comments ? comments.filter(d => !!d.text.trim()) : [];

    if (discussion.type === DiscussionType.quarterly) {
      this.quarterlyQuestions = this.mergeQuestions(quarterlyQuestions, formValue);
    } else if (discussion.type === DiscussionType.annual) {
      this.meetingQuestions = this.mergeQuestions(meetingQuestions, formValue);
    }
  }

  protected mergeQuestions(
    questions: DiscussionFormalQuestion[],
    formValue: DiscussionFormValue
  ): DiscussionFormalQuestion[] {
    return questions
      ? questions.map((q, idx): DiscussionFormalQuestion => {
          const question = formValue.customQuestions[idx];
          return {
            ...q,
            managerAnswer: question?.manager?.answer ?? '',
            manageeAnswer: question?.managee?.answer ?? '',
          };
        })
      : [];
  }

  protected mergeForm(
    target: DiscussionForm,
    source: DiscussionFormValue,
    sourceMergeKey: DiscussionFormSectionKey,
    discussion: DiscussionModel
  ): DiscussionForm {
    if (!target.leadershipAssessment || !target.managementAssessment) return target;
    return {
      ...target,
      coreValueNotes: source.coreValues[sourceMergeKey].notes,
      coreValueRatings: target.coreValueRatings.map((r, idx) => ({
        ...r,
        rating: source.coreValues[sourceMergeKey].ratings?.[idx] ?? ValueRating.EMPTY,
      })),
      roleNotes: source.roleRatings[sourceMergeKey].notes,
      roleRatings:
        discussion.bos === BusinessOperatingSystem.eos
          ? target.roleRatings
          : target.roleRatings.map((r, idx) => ({
              ...r,
              getsIt: source.roleRatings[sourceMergeKey].ratings?.[idx]?.getsIt ?? YesNoMaybe.EMPTY,
              wantsIt: source.roleRatings[sourceMergeKey].ratings?.[idx]?.wantsIt ?? YesNoMaybe.EMPTY,
              hasCapacity: source.roleRatings[sourceMergeKey].ratings?.[idx]?.hasCapacity ?? YesNoMaybe.EMPTY,
            })),
      seatRating:
        discussion.bos === BusinessOperatingSystem.eos
          ? source.roleRatings[sourceMergeKey].ratings[0]
          : { getsIt: YesNoMaybe.EMPTY, wantsIt: YesNoMaybe.EMPTY, hasCapacity: YesNoMaybe.EMPTY },
      leadershipCommitments: source.leadership[sourceMergeKey].ratings,
      leadershipAssessment: { ...target.leadershipAssessment, note: source.leadership[sourceMergeKey].notes },
      coachingCommitments: source.coaching[sourceMergeKey].ratings,
      managementAssessment: { ...target.managementAssessment, note: source.coaching[sourceMergeKey].notes },
    };
  }
}

export class SaveManagerDiscussionFormDto extends SaveDiscussionFormDto {
  override managerDiscussionForm!: DiscussionForm;
  override manageeDiscussionForm: undefined;

  constructor(discussion: DiscussionModel, formValue: DiscussionFormValue, finished = false) {
    super(discussion, formValue);

    if (finished) this.isManagerDone = true;

    if (!discussion.managerDiscussionForm) throw new Error('A managerDiscussionForm is required.');
    this.managerDiscussionForm = this.mergeForm(discussion.managerDiscussionForm, formValue, 'manager', discussion);
  }
}

export class SaveManageeDiscussionFormDto extends SaveDiscussionFormDto {
  override manageeDiscussionForm!: DiscussionForm;
  override managerDiscussionForm: undefined;

  constructor(discussion: DiscussionModel, formValue: DiscussionFormValue, finished = false) {
    super(discussion, formValue);

    if (finished) this.isManageeDone = true;

    if (!discussion.manageeDiscussionForm) throw new Error('A manageeDiscussionForm is required.');
    this.manageeDiscussionForm = this.mergeForm(discussion.manageeDiscussionForm, formValue, 'managee', discussion);
  }
}

export class SaveDiscussionFormDtoFactory {
  static create(config: {
    mode: DiscussionFormMode.Manager | DiscussionFormMode.Managee;
    discussion: DiscussionModel;
    formValue: DiscussionFormValue;
    finished?: boolean;
  }): SaveDiscussionFormDto {
    const { mode, discussion, formValue, finished } = config;
    switch (mode) {
      case DiscussionFormMode.Manager:
        return new SaveManagerDiscussionFormDto(discussion, formValue, finished);
      case DiscussionFormMode.Managee:
        return new SaveManageeDiscussionFormDto(discussion, formValue, finished);
      default:
        throw new Error('Invalid DiscussionFormMode');
    }
  }
}
